import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { useParams } from 'react-router-dom';
import TextField from '@mui/material/TextField';
import { useStores } from '../../hooks/use-stores';
import ThemedSpinner from '../../components/themed/ThemedSpinner';
import {
    Block,
    FlexDiv,
    Heading2,
    SpaceBetweenDiv,
    TextDefault,
    ThemedButton,
} from '@awarego-ui/components';
import TableClient from '../../components/table/TableClient';
import { extractErrorMessage } from '../../utils/helpers';
import services from '../../services';

function categoryLabel(category) {
    return String(category || 'other').replace(/_/g, ' ');
}

function formatWhen(ms) {
    const n = Number(ms);
    if (!Number.isFinite(n) || n <= 0) return '';
    return new Date(n).toLocaleDateString();
}

function PartnerSafeAction() {
    const { partnerId } = useParams();
    const { commonStore, partnerStore } = useStores();
    const [summary, setSummary] = useState(null);
    const [codes, setCodes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [count, setCount] = useState('10');
    const [dailyLimit, setDailyLimit] = useState('');
    const [creating, setCreating] = useState(false);
    const [latestCodes, setLatestCodes] = useState([]);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [summaryResult, promoResult] = await Promise.all([
                services.Partners.safeActionSummary(partnerId),
                services.Partners.safeActionPromos(partnerId),
            ]);
            setSummary(summaryResult || null);
            setCodes((promoResult && promoResult.codes) || []);
        } catch (e) {
            setSummary(null);
            setCodes([]);
            setError(
                extractErrorMessage(e) || 'Failed to load SafeAction data'
            );
        } finally {
            setLoading(false);
        }
    }, [partnerId]);

    useEffect(() => {
        void partnerStore.loadSettings(partnerId);
        void load();
    }, [partnerId, partnerStore, load]);

    const generate = async () => {
        const n = parseInt(count, 10);
        if (!Number.isFinite(n) || n < 1 || n > 500) {
            commonStore.error('Count must be between 1 and 500');
            return;
        }
        const payload = { count: n };
        if (String(dailyLimit).trim() !== '') {
            const limit = parseInt(dailyLimit, 10);
            if (!Number.isFinite(limit) || limit < 1 || limit > 1000) {
                commonStore.error('Daily limit must be between 1 and 1000');
                return;
            }
            payload.dailyLimit = limit;
        }
        setCreating(true);
        try {
            const created = await services.Partners.createSafeActionPromos(
                partnerId,
                payload
            );
            setLatestCodes((created && created.codes) || []);
            commonStore.success(
                `Created ${(created && created.codes && created.codes.length) || n} codes`
            );
            await load();
        } catch (e) {
            commonStore.error(
                extractErrorMessage(e) || 'Failed to create codes'
            );
        } finally {
            setCreating(false);
        }
    };

    const revoke = async (code) => {
        if (!window.confirm(`Revoke code ${code}? It can no longer be redeemed.`)) {
            return;
        }
        try {
            await services.Partners.deactivateSafeActionPromo(partnerId, code);
            commonStore.success(`Revoked ${code}`);
            await load();
        } catch (e) {
            commonStore.error(extractErrorMessage(e) || 'Failed to revoke code');
        }
    };

    const copyLatest = async () => {
        try {
            await navigator.clipboard.writeText(latestCodes.join('\n'));
            commonStore.success('Codes copied');
        } catch (e) {
            commonStore.error('Could not copy codes');
        }
    };

    const categoryColumns = useMemo(
        () => [
            {
                Header: 'Category',
                accessor: 'category',
                Cell: ({ value }) => categoryLabel(value),
            },
            { Header: 'Detections', accessor: 'n' },
        ],
        []
    );

    const codeColumns = useMemo(
        () => [
            { Header: 'Code', accessor: 'code' },
            { Header: 'Tier', accessor: 'tier' },
            {
                Header: 'Daily limit',
                accessor: 'dailyLimit',
                Cell: ({ value }) => (value == null ? 'Default' : value),
            },
            { Header: 'Redeemed', accessor: 'redemptions' },
            {
                Header: 'Status',
                accessor: 'active',
                Cell: ({ value }) => (value ? 'Active' : 'Revoked'),
            },
            {
                Header: 'Created',
                accessor: 'createdAt',
                Cell: ({ value }) => formatWhen(value),
            },
            {
                Header: ' ',
                id: 'actions',
                Cell: ({ row }) =>
                    row.original.active ? (
                        <ThemedButton
                            mode="secondary"
                            text="Revoke"
                            onClick={() => revoke(row.original.code)}
                        />
                    ) : null,
            },
        ],
        // revoke closes over partnerId; columns refresh when it changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [partnerId]
    );

    const totals = (summary && summary.totals) || {};
    const categories = (summary && summary.byCategory) || [];

    return (
        <Fragment>
            <Block>
                <Heading2 displayBlock>SafeAction</Heading2>
                <TextDefault lighter displayBlock>
                    Scans from people who redeemed this partner&apos;s codes,
                    last 30 days. Screenshots are not stored. Sponsored users
                    stay on your partner invoice.
                </TextDefault>
                {loading ? (
                    <ThemedSpinner />
                ) : error ? (
                    <TextDefault displayBlock mt={16}>
                        {error}
                    </TextDefault>
                ) : (
                    <Fragment>
                        <FlexDiv gap={24} mt={16}>
                            <TextDefault displayBlock>
                                Scans: {totals.scans ?? 0}
                            </TextDefault>
                            <TextDefault displayBlock>
                                Fraud: {totals.fraud ?? 0}
                            </TextDefault>
                            <TextDefault displayBlock>
                                Warning: {totals.warning ?? 0}
                            </TextDefault>
                            <TextDefault displayBlock>
                                Users: {totals.users ?? 0}
                            </TextDefault>
                        </FlexDiv>
                        {categories.length > 0 ? (
                            <TableClient
                                columns={categoryColumns}
                                data={categories}
                                hidePagination={categories.length < 10}
                                initialPageSize={10}
                            />
                        ) : (
                            <TextDefault lighter displayBlock mt={16}>
                                No threats recorded in the last 30 days.
                            </TextDefault>
                        )}
                    </Fragment>
                )}
            </Block>

            <Block mt={16}>
                <SpaceBetweenDiv ml={16} pr={32}>
                    <div>
                        <Heading2 displayBlock>Promo codes</Heading2>
                        <TextDefault lighter displayBlock>
                            A redeemed code grants the sponsored daily scan
                            allowance. Leave the daily limit empty to use the
                            default.
                        </TextDefault>
                    </div>
                </SpaceBetweenDiv>
                <FlexDiv gap={16} alignCenter mt={16} ml={16}>
                    <TextField
                        label="How many"
                        type="number"
                        variant="filled"
                        value={count}
                        onChange={(e) => setCount(e.target.value)}
                        inputProps={{ min: 1, max: 500 }}
                    />
                    <TextField
                        label="Daily limit"
                        type="number"
                        variant="filled"
                        value={dailyLimit}
                        onChange={(e) => setDailyLimit(e.target.value)}
                        inputProps={{ min: 1, max: 1000 }}
                    />
                    <ThemedButton
                        mode="primary"
                        text={creating ? 'Creating' : 'Generate codes'}
                        disabled={creating}
                        onClick={generate}
                    />
                </FlexDiv>
                {latestCodes.length > 0 && (
                    <div style={{ margin: '16px' }}>
                        <SpaceBetweenDiv>
                            <TextDefault displayBlock>
                                New codes ({latestCodes.length})
                            </TextDefault>
                            <ThemedButton
                                mode="secondary"
                                text="Copy"
                                onClick={copyLatest}
                            />
                        </SpaceBetweenDiv>
                        <TextDefault lighter displayBlock mt={8}>
                            {latestCodes.join(', ')}
                        </TextDefault>
                    </div>
                )}
                {loading ? (
                    <ThemedSpinner />
                ) : codes.length > 0 ? (
                    <TableClient
                        columns={codeColumns}
                        data={codes}
                        hidePagination={codes.length < 10}
                        initialPageSize={10}
                    />
                ) : (
                    !error && (
                        <TextDefault lighter ml={16} mt={16} displayBlock>
                            No codes yet.
                        </TextDefault>
                    )
                )}
            </Block>
        </Fragment>
    );
}

export default observer(PartnerSafeAction);
